import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Topic } from '@/types/content';
import { topics } from '@/mocks/topics';
import { categories } from '@/mocks/categories';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import TopicCard from '@/components/feature/TopicCard';
import Reveal from '@/components/feature/Reveal';
import { useSeo } from '@/hooks/useSeo';
import HeroSection from './components/ListHero';

const allTopics = topics as Topic[];

type SortKey = 'heat' | 'date';

const sortOptions: { key: SortKey; label: string; icon: string }[] = [
  { key: 'heat', label: '按热度', icon: 'ri-fire-line' },
  { key: 'date', label: '按最新', icon: 'ri-time-line' },
];

export default function FrontlinePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get('category') ?? 'all';
  const [keyword, setKeyword] = useState(searchParams.get('keyword') ?? '');
  const [sortKey, setSortKey] = useState<SortKey>('heat');

  useSeo({
    title: '围观前线 - 511群众瓜点列表｜吃瓜小队蹲点记录汇总',
    description:
      '511群众围观前线汇总吃瓜小队的全部瓜点记录，支持按主题筛选与关键词搜索，涵盖网络热榜、娱乐现场、活动现场、圈层动态与幕后花絮，快速找到你关心的那条瓜点。',
    keywords: '511群众围观前线,511群众瓜点列表,吃瓜小队蹲点记录,511群众抱团吃瓜,网络热榜汇总',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: '围观前线 - 511群众瓜点列表',
        description: '汇总吃瓜小队的全部瓜点记录，支持主题筛选与关键词搜索。',
        inLanguage: 'zh-CN',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://511qunzhong.space/' },
          { '@type': 'ListItem', position: 2, name: '围观前线', item: 'https://511qunzhong.space/frontline' },
        ],
      },
    ],
  });

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    const list = allTopics.filter((topic) => {
      const matchCategory = activeCategory === 'all' || topic.category === activeCategory;
      const matchKeyword =
        !kw ||
        topic.title.toLowerCase().includes(kw) ||
        topic.summary.toLowerCase().includes(kw) ||
        topic.tags.some((tag) => tag.toLowerCase().includes(kw));
      return matchCategory && matchKeyword;
    });
    return [...list].sort((a, b) =>
      sortKey === 'heat' ? b.heat - a.heat : b.date.localeCompare(a.date)
    );
  }, [activeCategory, keyword, sortKey]);

  const handleCategory = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id === 'all') {
      next.delete('category');
    } else {
      next.set('category', id);
    }
    setSearchParams(next, { replace: true });
  };

  const activeName =
    activeCategory === 'all'
      ? '全部瓜点'
      : categories.find((c) => c.id === activeCategory)?.name ?? '全部瓜点';

  return (
    <SiteLayout>
      <HeroSection activeName={activeName} total={filtered.length} />

      <section className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14">
        <Breadcrumbs
          items={[
            { label: '围观前线', to: activeCategory === 'all' ? undefined : '/frontline' },
            ...(activeCategory === 'all' ? [] : [{ label: activeName }]),
          ]}
        />

        <div className="mt-6 flex flex-col gap-5">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => handleCategory('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                activeCategory === 'all'
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              全部
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                type="button"
                onClick={() => handleCategory(category.id)}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  activeCategory === category.id
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                <i className={category.icon}></i>
                {category.name}
              </button>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="relative w-full sm:max-w-sm">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line"></i>
              </span>
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索瓜点标题、标签或关键词"
                aria-label="搜索瓜点"
                className="w-full pl-10 pr-3.5 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
              />
            </div>

            <div className="flex items-center gap-1 px-1 py-1 rounded-full bg-background-100 w-max">
              {sortOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setSortKey(option.key)}
                  className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm transition-colors whitespace-nowrap ${
                    sortKey === option.key
                      ? 'bg-background-50 text-foreground-950 font-medium'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  <i className={option.icon}></i>
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-8 flex items-center justify-between">
          <p className="text-sm text-foreground-500">
            共 <span className="font-medium text-foreground-900">{filtered.length}</span> 条瓜点记录
          </p>
          {keyword && (
            <button
              type="button"
              onClick={() => setKeyword('')}
              className="text-sm text-primary-700 hover:text-primary-800 transition-colors whitespace-nowrap"
            >
              清除搜索
            </button>
          )}
        </div>

        {filtered.length > 0 ? (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {filtered.map((topic, index) => (
              <Reveal key={topic.id} delay={(index % 3) * 80}>
                <TopicCard topic={topic} />
              </Reveal>
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center justify-center py-16 text-center rounded-lg border border-dashed border-background-300 bg-background-100/60">
            <span className="w-14 h-14 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 text-2xl">
              <i className="ri-search-eye-line"></i>
            </span>
            <h3 className="mt-4 font-heading font-semibold text-lg text-foreground-900">没找到对应的瓜点</h3>
            <p className="mt-2 text-sm text-foreground-600">换个关键词，或者先看看全部记录。</p>
            <button
              type="button"
              onClick={() => {
                setKeyword('');
                handleCategory('all');
              }}
              className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              查看全部瓜点
            </button>
          </div>
        )}
      </section>
    </SiteLayout>
  );
}